const HALF_PI = Math.PI / 2
const PI = Math.PI
const TRANSPARENT = 'rgba(0, 0, 0, 0)'

export function scaleByDevicePixelRatio(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const dpr = window.devicePixelRatio
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  canvas.width = width * dpr
  canvas.height = height * dpr
  context.scale(dpr, dpr)
}

export function computeTextWidth(ctx: CanvasRenderingContext2D, text: string, font: string) {
  const oldFont = ctx.font
  ctx.font = font
  const { width } = ctx.measureText(text)
  ctx.font = oldFont
  return width
}

export const saveRestore = (ctx: CanvasRenderingContext2D, fn: () => void) => {
  ctx.save()
  fn()
  ctx.restore()
}

interface RoundedRectOptions {
  color: string
  radius: number
  x: number
  y: number
  width: number
  height: number
}
export function roundedRect(ctx: CanvasRenderingContext2D, options: RoundedRectOptions) {
  const { x, y, width, height, radius, color } = options
  ctx.fillStyle = color

  ctx.beginPath()
  ctx.arc(radius + x, radius + y, radius, -HALF_PI, PI, true)
  ctx.lineTo(x, y + height - radius)
  ctx.arc(radius + x, height - radius + y, radius, PI, HALF_PI, true)
  ctx.lineTo(x + width - radius, y + height)
  ctx.arc(x + width - radius, y + height - radius, radius, HALF_PI, 0, true)
  ctx.lineTo(x + width, y + radius)
  ctx.arc(x + width - radius, y + radius, radius, 0, -HALF_PI, true)
  ctx.lineTo(x + radius, y)
  ctx.fill()
}

interface CircleOptions {
  radius: number
  cx: number
  cy: number
  fillColor?: string
  strokeColor?: string
  opacity?: number
}
export function circle(ctx: CanvasRenderingContext2D, options: CircleOptions) {
  const {
    cx,
    cy,
    radius,
    fillColor = TRANSPARENT,
    strokeColor = TRANSPARENT,
    opacity = 1,
  } = options

  saveRestore(ctx, () => {
    ctx.globalAlpha = opacity
    ctx.fillStyle = fillColor
    ctx.strokeStyle = strokeColor

    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, PI * 2, true)
    ctx.fill()
    ctx.stroke()
  })
}

interface CrossOptions {
  x: number
  y: number
  width: number
  height: number
  color?: string
}
export function cross(ctx: CanvasRenderingContext2D, options: CrossOptions) {
  const { x, y, width, height, color = 'black' } = options
  ctx.strokeStyle = color

  // \
  ctx.beginPath()
  ctx.moveTo(x, y)
  ctx.lineTo(x + width, y + height)
  ctx.stroke()
  // /
  ctx.beginPath()
  ctx.moveTo(x + width, y)
  ctx.lineTo(x, y + height)
  ctx.stroke()
}

interface RectOptions {
  x: number
  y: number
  width: number
  height: number
  fillColor?: string
  strokeColor?: string
}
export function rect(ctx: CanvasRenderingContext2D, options: RectOptions) {
  const { x, y, width, height, fillColor = TRANSPARENT, strokeColor = TRANSPARENT } = options
  ctx.fillStyle = fillColor
  ctx.strokeStyle = strokeColor

  ctx.fillRect(x, y, width, height)
  ctx.strokeRect(x, y, width, height)
  ctx.fill()
  ctx.stroke()
}

// prettier-ignore
export type Align =
  | 'top left'    | 'top center'    | 'top right'
  | 'center left' | 'center center' | 'center right'
  | 'bottom left' | 'bottom center' | 'bottom right'

export const computeAlignOffset = (
  width: number,
  height: number,
  align: Align
): [number, number] => {
  const [vAlign, hAlign] = align.split(' ').map((s) => s.toUpperCase())

  const xOff = {
    LEFT: 0,
    CENTER: -width / 2,
    RIGHT: -width,
  }[hAlign]

  const yOff = {
    TOP: height,
    CENTER: height / 2,
    BOTTOM: 0,
  }[vAlign]

  return [xOff, yOff]
}

interface LabelOptions {
  x: number
  y: number
  label: string
  align?: Align
  fontSize?: number
  showRect?: boolean
  lineColor?: string
}
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  { x, y, label, align = 'center left', fontSize = 12, showRect = true, lineColor }: LabelOptions
) {
  const padding = { top: 1, right: 2, bottom: 1, left: 2 }

  const line = { top: 1, stroke: 1.5 }
  const lineStroke = lineColor ? line.stroke : 0
  const lineSpace = lineColor ? lineStroke + line.top : 0

  ctx.font = `normal ${fontSize}px 'rational'`
  const { width: textWidth } = ctx.measureText(label)

  const boxWidth = padding.left + textWidth + padding.right
  const boxHeight = padding.top + fontSize + 3 + lineSpace + padding.bottom
  const [xBox, yBox] = computeAlignOffset(boxWidth, boxHeight, align)

  ctx.save()
  ctx.translate(x, y)

  // ctx.save()
  // ctx.strokeStyle = 'green'
  // ctx.stroke(new Path2D('M0,-50 L0,50 M-50,0 L50,0'))
  // ctx.restore()

  ctx.translate(xBox, yBox)

  // RECT
  if (showRect) {
    ctx.save()
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
    ctx.shadowBlur = 4
    roundedRect(ctx, {
      x: 0,
      y: -boxHeight,
      width: boxWidth,
      height: boxHeight,
      radius: 3,
      color: 'rgba(255, 255, 255, 0.9)',
    })
    ctx.restore()
  }

  // LINE
  if (lineColor) {
    ctx.save()
    ctx.strokeStyle = lineColor
    ctx.lineWidth = lineStroke

    ctx.translate(0, -3 - lineStroke / 2)
    ctx.beginPath()
    ctx.moveTo(lineStroke / 2 + padding.left, 0)
    ctx.lineTo(boxWidth - lineStroke / 2 - padding.right, 0)
    ctx.stroke()
    ctx.restore()
  }
  // TEXT
  ctx.save()
  ctx.fillStyle = 'black'
  ctx.font = `normal 12px 'rational'`
  ctx.fillText(label, padding.left, -5 - lineSpace)
  ctx.restore()

  ctx.restore()

  return { x: x + xBox, y: y + yBox, width: boxWidth, height: boxHeight }
}
